import React, { PureComponent } from 'react';

import debounce from 'lodash-es/debounce';
import get from 'lodash-es/get';
import { injectIntl, InjectedIntl } from 'react-intl';

import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import Select from '@atlaskit/select';
import { gridSize, layers } from '@atlaskit/theme';

// Use this directly instead of `src/containers/user-avatar` to skip profile card and link behavior
import { User } from 'src/components/types';
import { UserAvatar } from 'src/components/user-profile';
import { selectControlStyles } from 'src/styles/select';

import * as filterStyles from '../../../styles/filters';
import { AuthorOption, PRFilterState } from '../types';

import * as styles from './author-selector.styled';
import messages from './i18n';

const INPUT_CHANGE_DEBOUNCE_TIME = 200;

export type InjectedProps = {
  intl: InjectedIntl;
  isLoadingAuthors: boolean;
  isLoadingAuthor: boolean;
  isError: boolean;
  options: AuthorOption[];
  preloadedOption?: AuthorOption;
  fetchPullRequestAuthors: (
    repositoryFullSlug: string,
    status: (string | null)[],
    query?: string
  ) => void;
  fetchPullRequestAuthor: (uuid: string) => void;
  clearFilteredAuthors: () => void;
  updatePreloadedAuthor: (author: User | null) => void;
};

export type OwnProps = {
  repositoryFullSlug: string;
  uuid?: string;
  onChange: (uuid: string | null) => void;
  prStatus: PRFilterState[];
};

type Props = InjectedProps & OwnProps;

class AuthorSelector extends PureComponent<Props> {
  componentDidMount() {
    this.syncPreloadedAuthorWithUuid();
    this.props.fetchPullRequestAuthors(
      this.props.repositoryFullSlug,
      this.getPullRequestStatus(this.props.prStatus)
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.uuid !== this.props.uuid) {
      this.syncPreloadedAuthorWithUuid();
    }
    if (prevProps.prStatus !== this.props.prStatus) {
      this.props.fetchPullRequestAuthors(
        this.props.repositoryFullSlug,
        this.getPullRequestStatus(this.props.prStatus)
      );
    }
  }

  syncPreloadedAuthorWithUuid() {
    const { uuid, preloadedOption } = this.props;
    if (!uuid || get(preloadedOption, 'value.uuid') === uuid) {
      return;
    }
    this.props.fetchPullRequestAuthor(uuid);
  }

  formatOptionLabel = (option: AuthorOption) => {
    const { value: author, label } = option;
    if (author) {
      return (
        <styles.AuthorOption>
          <UserAvatar size="small" user={author} />
          <filterStyles.Ellipsis>{label}</filterStyles.Ellipsis>
        </styles.AuthorOption>
      );
    }
    return null;
  };

  getPullRequestStatus = (status: PRFilterState[]) => {
    const statuses = {
      OPEN: 'open',
      MERGED: 'fulfilled',
      DECLINED: 'rejected',
      DRAFT: 'draft',
      SUPERSEDED: null,
      ALL: null,
    };
    return status.map(state => statuses[state]);
  };

  getNoOptionsMessage = () => {
    const { intl, isError } = this.props;
    if (isError) {
      return intl.formatMessage(messages.filterAuthorError);
    }
    return intl.formatMessage(messages.filterAuthorNoOptions);
  };

  handleChange = (option: AuthorOption) => {
    this.props.updatePreloadedAuthor(option?.value || null);
    this.props.onChange(option?.value?.uuid || null);
  };

  // @ts-ignore TODO: fix noImplicitAny error here
  handleInputChange = (inputValue: string, { action }) => {
    const { repositoryFullSlug } = this.props;
    if (action === 'input-change') {
      this.props.fetchPullRequestAuthors(
        repositoryFullSlug,
        this.getPullRequestStatus(this.props.prStatus),
        inputValue
      );
      return;
    }
    if (action === 'menu-close') {
      this.props.clearFilteredAuthors();
    }
  };

  handleInputChangeDebounce = debounce(
    this.handleInputChange,
    INPUT_CHANGE_DEBOUNCE_TIME
  );

  render() {
    const {
      intl,
      isLoadingAuthors,
      isLoadingAuthor,
      options,
      preloadedOption,
    } = this.props;
    return (
      <UFOLoadHold name="author-selector" hold={isLoadingAuthors}>
        <div data-qa="select-pull-request-author">
          <Select
            isClearable
            blurInputOnSelect
            isLoading={isLoadingAuthors || isLoadingAuthor}
            isDisabled={isLoadingAuthor}
            options={options}
            placeholder={intl.formatMessage(messages.filterAuthor)}
            formatOptionLabel={this.formatOptionLabel}
            noOptionsMessage={this.getNoOptionsMessage}
            onChange={this.handleChange}
            onInputChange={this.handleInputChangeDebounce}
            value={
              isLoadingAuthor
                ? {
                    label: intl.formatMessage(messages.filterAuthorLoading),
                    value: null,
                  }
                : preloadedOption
            }
            styles={{
              control: (css, state) => ({
                ...css,
                ...filterStyles.subtleSelector(css, state),
                ...selectControlStyles,
                minWidth:
                  preloadedOption || isLoadingAuthor
                    ? gridSize() * 23
                    : gridSize() * 13,
                maxWidth: gridSize() * 23,
              }),
              menu: css => ({
                ...css,
                minWidth: gridSize() * 32,
                zIndex: layers.navigation(),
              }),
              // @ts-ignore TODO: fix noImplicitAny error here
              placeholder: css => ({
                ...css,
                ...filterStyles.nothingSelectedColor(),
              }),
            }}
          />
        </div>
      </UFOLoadHold>
    );
  }
}

export default injectIntl(AuthorSelector);
